import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import {
  updateProgramAiInstructions,
  type UpdateProgramAiInstructionsParams,
  type UpdateProgramAiInstructionsResult,
  updateProgramStep0Flags,
  type UpdateProgramStep0FlagsParams,
  type UpdateProgramStep0FlagsResult,
  updateProgramRequirementsQc,
  type UpdateProgramRequirementsQcParams,
  type UpdateProgramRequirementsQcResult,
  updateProgramDeadlinesQc,
  type UpdateProgramDeadlinesQcParams,
  type UpdateProgramDeadlinesQcResult,
  updateProgramJudgingQc,
  type UpdateProgramJudgingQcParams,
  type UpdateProgramJudgingQcResult,
  updateProgramDetailsQc,
  type UpdateProgramDetailsQcParams,
  type UpdateProgramDetailsQcResult,
  updateProgramVettingQc,
  type UpdateProgramVettingQcParams,
  type UpdateProgramVettingQcResult,
  updateProgramDetailsData,
  type UpdateProgramDetailsDataParams,
  type UpdateProgramDetailsDataResult
} from '../services/rpc/programDetails';

export function useUpdateProgramAiInstructions(
  options?: UseMutationOptions<
    UpdateProgramAiInstructionsResult,
    Error,
    UpdateProgramAiInstructionsParams
  >
): UseMutationResult<UpdateProgramAiInstructionsResult, Error, UpdateProgramAiInstructionsParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramAiInstructionsResult, Error, UpdateProgramAiInstructionsParams>({
    mutationFn: updateProgramAiInstructions,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export function useUpdateProgramStep0Flags(
  options?: UseMutationOptions<
    UpdateProgramStep0FlagsResult,
    Error,
    UpdateProgramStep0FlagsParams
  >
): UseMutationResult<UpdateProgramStep0FlagsResult, Error, UpdateProgramStep0FlagsParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramStep0FlagsResult, Error, UpdateProgramStep0FlagsParams>({
    mutationFn: updateProgramStep0Flags,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export function useUpdateProgramRequirementsQc(
  options?: UseMutationOptions<
    UpdateProgramRequirementsQcResult,
    Error,
    UpdateProgramRequirementsQcParams
  >
): UseMutationResult<UpdateProgramRequirementsQcResult, Error, UpdateProgramRequirementsQcParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramRequirementsQcResult, Error, UpdateProgramRequirementsQcParams>({
    mutationFn: updateProgramRequirementsQc,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export function useUpdateProgramDeadlinesQc(
  options?: UseMutationOptions<
    UpdateProgramDeadlinesQcResult,
    Error,
    UpdateProgramDeadlinesQcParams
  >
): UseMutationResult<UpdateProgramDeadlinesQcResult, Error, UpdateProgramDeadlinesQcParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramDeadlinesQcResult, Error, UpdateProgramDeadlinesQcParams>({
    mutationFn: updateProgramDeadlinesQc,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export function useUpdateProgramJudgingQc(
  options?: UseMutationOptions<
    UpdateProgramJudgingQcResult,
    Error,
    UpdateProgramJudgingQcParams
  >
): UseMutationResult<UpdateProgramJudgingQcResult, Error, UpdateProgramJudgingQcParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramJudgingQcResult, Error, UpdateProgramJudgingQcParams>({
    mutationFn: updateProgramJudgingQc,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export function useUpdateProgramDetailsQc(
  options?: UseMutationOptions<
    UpdateProgramDetailsQcResult,
    Error,
    UpdateProgramDetailsQcParams
  >
): UseMutationResult<UpdateProgramDetailsQcResult, Error, UpdateProgramDetailsQcParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramDetailsQcResult, Error, UpdateProgramDetailsQcParams>({
    mutationFn: updateProgramDetailsQc,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export function useUpdateProgramVettingQc(
  options?: UseMutationOptions<
    UpdateProgramVettingQcResult,
    Error,
    UpdateProgramVettingQcParams
  >
): UseMutationResult<UpdateProgramVettingQcResult, Error, UpdateProgramVettingQcParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramVettingQcResult, Error, UpdateProgramVettingQcParams>({
    mutationFn: updateProgramVettingQc,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });

      queryClient.invalidateQueries({
        queryKey: ['program-search']
      });
    },
    ...options
  });
}

export function useUpdateProgramDetailsData(
  options?: UseMutationOptions<
    UpdateProgramDetailsDataResult,
    Error,
    UpdateProgramDetailsDataParams
  >
): UseMutationResult<UpdateProgramDetailsDataResult, Error, UpdateProgramDetailsDataParams> {
  const queryClient = useQueryClient();

  return useMutation<UpdateProgramDetailsDataResult, Error, UpdateProgramDetailsDataParams>({
    mutationFn: updateProgramDetailsData,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['program-report-card']
      });

      queryClient.invalidateQueries({
        queryKey: ['activity-log', variables.program_id]
      });
    },
    ...options
  });
}

export default useUpdateProgramAiInstructions;
